/**
 * Minimale JWT-Auswertung im Browser.
 *
 * ⚠️ Hier wird NICHTS geprüft, was Sicherheit herstellt. Signatur, Aussteller
 * und Zielgruppe prüft ausschliesslich das Backend (`JwtValidator`). Diese
 * Datei liest allein das Ablaufdatum, damit die Navigation nicht erst über
 * einen 401-Umweg merkt, dass die Sitzung vorbei ist. Ein manipulierter Token
 * kommt damit keinen Schritt weiter - er scheitert beim ersten Aufruf.
 */

interface JwtPayload {
  exp?: unknown
}

/** Base64url-Rumpf eines JWT nach JSON auflösen. `null`, wenn unlesbar. */
export function decodeJwtPayload(jwt: string): JwtPayload | null {
  if (!jwt) {
    return null
  }
  const parts = jwt.split('.')
  if (parts.length < 2) {
    return null
  }
  try {
    // Base64url -> Base64, danach auf ein Vielfaches von 4 auffüllen.
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    while (base64.length % 4 !== 0) {
      base64 += '='
    }
    // decodeURIComponent/escape statt atob allein: sonst zerfallen Umlaute im
    // Namen des Benutzers, die Auth0 im Token mitschickt.
    const json = decodeURIComponent(
      Array.prototype.map
        .call(
          atob(base64),
          (char: string) =>
            '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2)
        )
        .join('')
    )
    return JSON.parse(json) as JwtPayload
  } catch {
    return null
  }
}

/**
 * Ist der Token abgelaufen?
 *
 * Bewusst wohlwollend: Lässt sich der Token nicht lesen oder fehlt `exp`,
 * lautet die Antwort `false`. Dann entscheidet das Backend. Ein hier
 * erfundenes "abgelaufen" würde Benutzer aussperren, weil wir ein Format nicht
 * verstehen - der umgekehrte Fehler kostet nur einen 401, der ohnehin sauber
 * behandelt wird.
 */
export function isJwtExpired(jwt: string): boolean {
  const payload = decodeJwtPayload(jwt)
  if (!payload || typeof payload.exp !== 'number' || !isFinite(payload.exp)) {
    return false
  }
  // `exp` ist in Sekunden seit 1970, Date.now() in Millisekunden.
  return payload.exp * 1000 <= Date.now()
}

/** Ablaufzeitpunkt als Datum, für Protokollausgaben. `null` wenn unbekannt. */
export function jwtExpiresAt(jwt: string): Date | null {
  const payload = decodeJwtPayload(jwt)
  if (!payload || typeof payload.exp !== 'number' || !isFinite(payload.exp)) {
    return null
  }
  return new Date(payload.exp * 1000)
}
